import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Loader2, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';

/**
 * Monthly Archive — Dipali · 05-Feb-2026 session 2.
 *
 * Tab strip [Apr 2026] [May 2026] [Jun 2026] ... per FY.
 * Click a month → revenue per outlet · covers · top dishes · kitchen
 * breakdown · YoY comparison.
 *
 * Palette fix (5 May 2026 · Dipali mobile unreadability report):
 * shipped v1 was dark-theme (bg-slate-900, text-slate-100) which
 * rendered as an illegible grey wash on ScaleBoard's light cream/navy
 * palette. Rewritten to canonical tokens:
 *   card bg  = white
 *   text     = #1B2A4A (navy) · #6B7280 (secondary) · #9CA3AF (muted)
 *   border   = #E5E1DC
 *   positive = #10B981 / #059669
 *   negative = #D4726A / #BA3F3F
 *   sub-bg   = #F7F5F2 (cream)
 */
const fmtINR = (v) => {
  if (v == null || isNaN(v)) return '—';
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${Math.round(v)}`;
};

export default function MonthlyArchive() {
  const [months, setMonths] = useState([]);
  const [activeMonth, setActiveMonth] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get('/division-monthly/me/months')
      .then((res) => {
        if (cancel) return;
        const list = res.data?.months || [];
        setMonths(list);
        const cur = list.find((m) => m.is_current) || list[list.length - 1];
        if (cur) setActiveMonth(cur.yyyy_mm);
      })
      .catch((e) => !cancel && setError(e.message || 'Failed to load months'))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!activeMonth) return;
    let cancel = false;
    setDetail(null);
    api.get(`/division-monthly/me/${activeMonth}`)
      .then((res) => !cancel && setDetail(res.data))
      .catch((e) => !cancel && setError(e.message || 'Failed to load month detail'));
    return () => { cancel = true; };
  }, [activeMonth]);

  if (loading) {
    return (
      <section
        data-testid="monthly-archive"
        className="mt-12 p-8 rounded-xl bg-white border"
        style={{ borderColor: '#E5E1DC' }}
      >
        <div className="flex items-center gap-3 text-sm" style={{ color: '#6B7280' }}>
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading monthly archive…
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="monthly-archive"
        className="mt-12 p-8 rounded-xl border"
        style={{ backgroundColor: '#FFF5F4', borderColor: '#D4726A40' }}
      >
        <div className="flex items-center gap-3 text-sm" style={{ color: '#BA3F3F' }}>
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      </section>
    );
  }

  return (
    <section data-testid="monthly-archive" className="mt-12">
      <header className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-semibold" style={{ color: '#1B2A4A' }}>
          Monthly Archive
          <span className="ml-3 text-xs font-normal" style={{ color: '#9CA3AF' }}>
            Live eZee · per outlet · YoY when 12mo data available
          </span>
        </h3>
      </header>

      {/* Tab strip */}
      <div
        data-testid="monthly-archive-tabs"
        className="flex gap-2 overflow-x-auto pb-2 mb-4"
      >
        {months.map((m) => {
          const isActive = activeMonth === m.yyyy_mm;
          return (
            <button
              key={m.yyyy_mm}
              data-testid={`monthly-archive-tab-${m.yyyy_mm}`}
              onClick={() => setActiveMonth(m.yyyy_mm)}
              className={`px-4 py-2 text-sm rounded-md whitespace-nowrap transition-colors border ${!m.has_data ? 'opacity-50' : ''}`}
              style={
                isActive
                  ? { backgroundColor: '#1B2A4A', color: '#FFFFFF', borderColor: '#1B2A4A' }
                  : { backgroundColor: '#FFFFFF', color: '#1B2A4A', borderColor: '#E5E1DC' }
              }
            >
              {m.label}
              {m.is_current && (
                <span
                  className="ml-1.5 text-[10px] uppercase tracking-wider"
                  style={{ color: isActive ? '#10B981' : '#059669' }}
                >
                  live
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Detail card */}
      {detail ? (
        <MonthDetailCard detail={detail} />
      ) : (
        <div
          className="text-sm p-6 rounded-xl border"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E1DC', color: '#6B7280' }}
        >
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
          Loading {activeMonth}…
        </div>
      )}
    </section>
  );
}

function MonthDetailCard({ detail }) {
  const { totals, outlets, top_dishes, kitchen_breakdown, yoy_comparison, label, doctrine } = detail;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Headline + outlets */}
      <div
        data-testid="monthly-archive-outlets"
        className="md:col-span-2 p-5 rounded-xl bg-white border"
        style={{ borderColor: '#E5E1DC' }}
      >
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <div>
            <div
              className="text-xs uppercase tracking-wider"
              style={{ color: '#9CA3AF' }}
            >
              {label}
            </div>
            <div
              className="text-3xl font-bold mt-1"
              style={{ color: '#1B2A4A' }}
            >
              {fmtINR(totals.revenue_net)}
            </div>
            <div className="text-xs mt-1" style={{ color: '#6B7280' }}>
              {totals.covers.toLocaleString()} covers · {totals.outlet_count}/{totals.outlet_total || 6} outlets active
            </div>
          </div>
          {yoy_comparison.available && (
            <div
              className="flex items-center gap-1.5 text-sm font-medium"
              style={{ color: yoy_comparison.growth_pct >= 0 ? '#059669' : '#BA3F3F' }}
            >
              {yoy_comparison.growth_pct >= 0
                ? <TrendingUp className="w-4 h-4" />
                : <TrendingDown className="w-4 h-4" />}
              {yoy_comparison.growth_pct >= 0 ? '+' : ''}{yoy_comparison.growth_pct}% YoY
            </div>
          )}
        </div>

        <table className="w-full text-sm">
          <thead
            className="text-xs uppercase tracking-wider border-b"
            style={{ color: '#9CA3AF', borderColor: '#E5E1DC' }}
          >
            <tr>
              <th className="text-left pb-2 pr-2">Outlet</th>
              <th className="text-right pb-2 px-2">Net Revenue</th>
              <th className="text-right pb-2 px-2">Covers</th>
              <th className="text-right pb-2 pl-2">Receipts</th>
            </tr>
          </thead>
          <tbody style={{ color: '#1B2A4A' }}>
            {outlets.map((o) => (
              <tr
                key={o.outlet_id}
                className="border-b"
                style={{ borderColor: '#F0EDE8' }}
              >
                <td className="py-2.5 pr-2">{o.outlet_name}</td>
                <td
                  className="text-right font-mono px-2"
                  style={{ color: '#1B2A4A', fontWeight: 500 }}
                >
                  {fmtINR(o.revenue_net)}
                </td>
                <td
                  className="text-right font-mono px-2"
                  style={{ color: '#6B7280' }}
                >
                  {o.covers.toLocaleString()}
                </td>
                <td
                  className="text-right font-mono pl-2 text-xs"
                  style={{ color: '#9CA3AF' }}
                >
                  {o.receipts}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!yoy_comparison.available && (
          <p className="mt-4 text-xs italic" style={{ color: '#9CA3AF' }}>
            YoY comparison: {yoy_comparison.note}.
          </p>
        )}
      </div>

      {/* Sidebar: top dishes + kitchen breakdown */}
      <div className="space-y-4">
        <div
          data-testid="monthly-archive-top-dishes"
          className="p-5 rounded-xl bg-white border"
          style={{ borderColor: '#E5E1DC' }}
        >
          <div
            className="text-xs uppercase tracking-wider mb-3"
            style={{ color: '#9CA3AF' }}
          >
            Top Dishes
          </div>
          {top_dishes.length === 0 ? (
            <div className="text-sm italic" style={{ color: '#9CA3AF' }}>
              Item-level data still ingesting
            </div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {top_dishes.slice(0, 8).map((d) => {
                const safeName = d.name && d.name.trim() ? d.name : '— (unnamed)';
                return (
                  <li key={safeName} className="flex justify-between gap-4">
                    <span
                      className="truncate"
                      style={{ color: d.name && d.name.trim() ? '#1B2A4A' : '#9CA3AF' }}
                    >
                      {safeName}
                    </span>
                    <span
                      className="font-mono text-xs whitespace-nowrap"
                      style={{ color: '#6B7280' }}
                    >
                      {d.qty}× · {fmtINR(d.revenue)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div
          data-testid="monthly-archive-kitchen"
          className="p-5 rounded-xl bg-white border"
          style={{ borderColor: '#E5E1DC' }}
        >
          <div
            className="text-xs uppercase tracking-wider mb-3"
            style={{ color: '#9CA3AF' }}
          >
            Kitchen Breakdown
          </div>
          <ul className="space-y-2 text-sm">
            <li className="flex justify-between">
              <span style={{ color: '#6B7280' }}>Food</span>
              <span className="font-mono" style={{ color: '#1B2A4A' }}>
                {fmtINR(kitchen_breakdown.food)}
              </span>
            </li>
            <li className="flex justify-between">
              <span style={{ color: '#6B7280' }}>Beverage</span>
              <span className="font-mono" style={{ color: '#1B2A4A' }}>
                {fmtINR(kitchen_breakdown.beverage)}
              </span>
            </li>
            <li className="flex justify-between">
              <span style={{ color: '#6B7280' }}>Consumable</span>
              <span className="font-mono" style={{ color: '#1B2A4A' }}>
                {fmtINR(kitchen_breakdown.consumable)}
              </span>
            </li>
            <li
              className="flex justify-between border-t pt-2 mt-2"
              style={{ borderColor: '#E5E1DC' }}
            >
              <span className="font-medium" style={{ color: '#1B2A4A' }}>
                Total Purchases
              </span>
              <span
                className="font-mono font-semibold"
                style={{ color: '#1B2A4A' }}
              >
                {fmtINR(kitchen_breakdown.total)}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <p
        className="md:col-span-3 text-[11px] italic mt-2"
        style={{ color: '#9CA3AF' }}
      >
        Source: {doctrine.revenue_source}. {doctrine.stays_excluded && doctrine.stays_note}
      </p>
    </div>
  );
}
